<template>
  <span class="frbtn" :class="{ 'frbtn--active': active }">
    <f7-link
      ref="btnRef"
      class="frbtn-btn"
      :aria-label="$t('feed.react')"
      @mouseenter="onHoverEnter"
      @mouseleave="onHoverLeave"
      @mousedown="startHold"
      @mouseup="endHold"
      @touchstart.passive="startHold"
      @touchend="endHold"
      @touchcancel="cancelHold"
      @click="quickReact"
    >
      <f7-icon v-if="active" :f7="activeIcon" :size="size" />
      <f7-icon v-else f7="hand_thumbsup" :size="size" />
      <span v-if="count > 0" class="frbtn-count">{{ count }}</span>
    </f7-link>
  </span>

  <Teleport to="body">
    <div
      v-if="open"
      ref="popRef"
      class="frbtn-pop"
      :style="popStyle"
      @mouseenter="onPopEnter"
      @mouseleave="onPopLeave"
    >
      <f7-link
        v-for="opt in options"
        :key="opt.type"
        class="frbtn-opt"
        :class="{ 'frbtn-opt--active': reactionType === opt.type }"
        @click="pick(opt.type)"
      >
        <span class="frbtn-emoji">{{ opt.emoji }}</span>
        <span class="frbtn-label">{{ opt.label }}</span>
      </f7-link>
      <f7-link
        v-if="active"
        class="frbtn-opt frbtn-opt--remove"
        @click="pick(null)"
      >
        <f7-icon f7="minus_circle" size="13" color="#C0440A" />
        <span class="frbtn-label">{{ $t('feed.remove') }}</span>
      </f7-link>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, nextTick, onBeforeUnmount, watch } from "vue";
import i18n from '../../js/i18n.js'
import { reactionIcon } from "../../js/feedReactions.js";

const { t } = i18n.global

const props = defineProps({
  reactionType: { type: String, default: null },
  count: { type: Number, default: 0 },
  options: { type: Array, default: () => [] },
  size: { type: Number, default: 10 },
});

const emit = defineEmits(["react"]);
const active = computed(() => !!props.reactionType);
const activeIcon = computed(() => reactionIcon(props.reactionType));
const open = ref(false);
const popRef = ref(null);
const btnRef = ref(null);
const popStyle = ref({});
let hoverTimer = null;
let closeTimer = null;
let holdTimer = null;
let suppressClick = false;

const canHover =
  typeof window !== "undefined" &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

function endHold() {
  clearTimeout(holdTimer);
}
function cancelHold() {
  clearTimeout(holdTimer);
}
function startHold(){
  if (canHover) return;
  holdTimer = setTimeout(() => {
    suppressClick = true;
    open.value = true;
  }, 350);
}
const MARGIN = 8;

function updatePopoverPosition() {
  const el = popRef.value;
  if (!el) { popStyle.value = {}; return; }

  const btnEl = btnRef.value;
  if (!btnEl || !btnEl.$el) { popStyle.value = {}; return; }
  const btnRect = btnEl.$el.getBoundingClientRect();

  const vw = window.innerWidth;
  const popW = el.offsetWidth || 0;
  const popH = el.offsetHeight || 0;

  let left = btnRect.left + btnRect.width / 2 - popW / 2;
  if (left < MARGIN) left = MARGIN;
  else if (left + popW > vw - MARGIN) left = vw - MARGIN - popW;

  const topAbove = btnRect.top - popH - 8;
  const topBelow = btnRect.bottom + 8;
  const top = topAbove >= MARGIN ? topAbove : topBelow;

  popStyle.value = {
    position: 'fixed',
    left: `${left}px`,
    top: `${top}px`,
    zIndex: 20000,
  };
}

watch(open, (v) => {
  if (v) nextTick(updatePopoverPosition);
});

function onHoverEnter() {
  if (!canHover) return;
  clearTimeout(closeTimer);
  clearTimeout(hoverTimer);
  hoverTimer = setTimeout(() => {
    if (!suppressClick) open.value = true;
  }, 250);
}
function onHoverLeave() {
  if (!canHover) return;
  clearTimeout(hoverTimer);
  startCloseTimer();
}
function onPopEnter() {
  if (!canHover) return;
  clearTimeout(closeTimer);
}
function onPopLeave() {
  if (!canHover) return;
  startCloseTimer();
}
function startCloseTimer() {
  clearTimeout(closeTimer);
  closeTimer = setTimeout(() => {
    open.value = false;
  }, 200);
}

function quickReact() {
  clearTimeout(hoverTimer);
  clearTimeout(closeTimer);
  clearTimeout(holdTimer);
  if (suppressClick) {
    suppressClick = false;
    return;
  }
  open.value = false;
  emit("react", active.value ? null : "LIKE");
}

function pick(type) {
  open.value = false;
  suppressClick = false;
  emit("react", type);
}

onBeforeUnmount(() => {
  clearTimeout(hoverTimer);
  clearTimeout(closeTimer);
  clearTimeout(holdTimer);
});
</script>

<style>
.frbtn-pop {
  position: fixed;
  display: flex;
  gap: 2px;
  background: var(--f7-page-bg-color, #fff);
  border-radius: 24px;
  padding: 6px 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  border: 0.5px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  white-space: nowrap;
  pointer-events: auto;
}
.frbtn-opt {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  background: none;
  border: none;
  padding: 4px 6px;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.15s;
}
.frbtn-opt:active {
  background: var(--f7-list-bg-color, #f5f5f5);
}
.frbtn-opt--active {
  background: var(--f7-list-bg-color, #f0f0f0);
}
.frbtn-opt--remove {
  flex-direction: row;
  padding: 4px 10px;
}
.frbtn-emoji {
  font-size: 20px;
  line-height: 1;
}
.frbtn-label {
  font-size: 8px;
  color: var(--f7-on-surface-variant, #8e8e93);
}
</style>

<style scoped>
.frbtn {
  position: relative;
  display: inline-flex;
}
.frbtn-btn {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  color: var(--f7-on-surface-variant, #8e8e93);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}
.frbtn--active .frbtn-btn {
  color: var(--avocash-green, #1a6b42);
  font-weight: 500;
}
.frbtn-count {
  font-size: 10px;
}
</style>
